import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, MessageSquare, Phone, User as UserIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

function formatPhone(phone: string) {
  const clean = (phone || "").replace(/\D/g, "");
  if (clean.length === 13) return `+${clean.slice(0, 2)} ${clean.slice(2, 4)} ${clean.slice(4, 9)}-${clean.slice(9)}`;
  if (clean.length === 12) return `+${clean.slice(0, 2)} ${clean.slice(2, 4)} ${clean.slice(4, 8)}-${clean.slice(8)}`;
  return phone;
}

type ProfileData = {
  jid: string | null;
  phone_number: string;
  push_name: string | null;
  avatar_url: string | null;
  connected: boolean;
  contacts_count: number;
  messages_count: number;
};

export function WhatsappProfileCard({ instanceId, fallbackPhone }: { instanceId: string; fallbackPhone: string }) {
  const { data, isLoading } = useQuery<ProfileData>({
    queryKey: ["instance_profile", instanceId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-instance-profile", {
        body: { instance_id: instanceId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data as ProfileData;
    },
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  const phone = data?.phone_number || fallbackPhone;
  const name = data?.push_name || "Sem nome no WhatsApp";

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
      <Card className="glass-card border-warmz-glass-border/50 overflow-hidden">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            {/* Avatar */}
            {isLoading ? (
              <Skeleton className="h-20 w-20 rounded-full shrink-0" />
            ) : (
              <Avatar className="h-20 w-20 ring-2 ring-primary/30 shrink-0">
                {data?.avatar_url && <AvatarImage src={data.avatar_url} alt={name} />}
                <AvatarFallback className="bg-primary/10 text-primary">
                  <UserIcon className="h-8 w-8" />
                </AvatarFallback>
              </Avatar>
            )}

            {/* Identity */}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                Perfil do WhatsApp
              </p>
              {isLoading ? (
                <>
                  <Skeleton className="h-5 w-40 mb-2" />
                  <Skeleton className="h-4 w-32" />
                </>
              ) : (
                <>
                  <h3 className="font-display text-lg font-bold text-foreground truncate">{name}</h3>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                    <Phone className="h-3.5 w-3.5" />
                    <span className="font-mono">{formatPhone(phone)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Stats */}
            <div className="flex gap-4 sm:gap-6 sm:border-l sm:border-warmz-glass-border/50 sm:pl-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 text-muted-foreground">
                  <Users className="h-3.5 w-3.5" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold">Contatos</span>
                </div>
                {isLoading ? (
                  <Skeleton className="h-7 w-12 mx-auto mt-1" />
                ) : (
                  <p className="font-display text-xl font-bold text-foreground mt-0.5">
                    {data?.contacts_count?.toLocaleString("pt-BR") ?? 0}
                  </p>
                )}
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 text-muted-foreground">
                  <MessageSquare className="h-3.5 w-3.5" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold">Mensagens</span>
                </div>
                {isLoading ? (
                  <Skeleton className="h-7 w-12 mx-auto mt-1" />
                ) : (
                  <p className="font-display text-xl font-bold text-foreground mt-0.5">
                    {data?.messages_count?.toLocaleString("pt-BR") ?? 0}
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
